/**
 * The rail: the application's sidebar.
 *
 * Dark in both themes and full height, and the one place the brand orange is
 * used as a large fill — the current screen is a solid orange pill. Top to
 * bottom it answers where you are (the property), where you can go (the
 * navigation) and who you are (the signed-in user). It collapses to an icon
 * strip for the screens that want the width, which is mostly Desk View, and
 * is always collapsed below 1024px, where it used to disappear altogether and
 * leave a tablet-sized window with no navigation at all.
 *
 * The rules from docs/08-uiux.md §3 still hold because the rail draws
 * `visibleSections`, never SECTIONS: a section with nothing in it the user may
 * open is not drawn at all, and there is no third level.
 */

import { LogOut, Settings } from 'lucide-react'
import { Link, useRouter } from '@/app/router'
import { HELP_ITEM, isCurrent, type NavItem, type NavSection } from '@/app/navigation'
import { useAuth } from '@/lib/auth'
import { PropertySwitcher } from '@/features/auth/PropertySwitcher'
import { cn, initials } from '@/lib/utils'

export function Rail({
  sections,
  home,
  collapsed,
}: {
  sections: NavSection[]
  home: string
  collapsed: boolean
}) {
  const { path } = useRouter()

  return (
    <aside
      className={cn(
        'rail sticky top-0 z-40 hidden h-screen shrink-0 flex-col md:flex',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-[76px]' : 'w-[256px]',
      )}
    >
      <PropertySwitcher home={home} collapsed={collapsed} />

      <nav
        aria-label="Main"
        className="scroll-thin flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4 pt-3"
      >
        {sections.map((section, index) => (
          <div key={section.label} className={cn(index > 0 && (collapsed ? 'mt-3' : 'mt-5'))}>
            {collapsed ? (
              index > 0 && (
                <div className="mx-auto mb-3 h-px w-8 bg-[var(--rail-border)]" aria-hidden />
              )
            ) : (
              <div className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--rail-muted)]">
                {section.label}
              </div>
            )}
            <ul className="flex flex-col gap-1">
              {section.items.map((item) => (
                <li key={item.to}>
                  <RailLink item={item} active={isCurrent(item, path)} collapsed={collapsed} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Help is for every role, so it is not in a section: pinned here, above
          who you are, it is in the same place whatever the role can see. */}
      <div className="px-3 pb-3">
        <RailLink item={HELP_ITEM} active={isCurrent(HELP_ITEM, path)} collapsed={collapsed} />
      </div>

      <RailUser collapsed={collapsed} />
    </aside>
  )
}

function RailLink({
  item,
  active,
  collapsed,
}: {
  item: NavItem
  active: boolean
  collapsed: boolean
}) {
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      // The label is still in the link when collapsed, for a screen reader;
      // the title is the sighted equivalent.
      title={collapsed ? item.label : undefined}
      className={cn(
        'flex items-center gap-3 rounded-[10px] text-[14px] transition-colors duration-150',
        collapsed ? 'mx-auto h-10 w-10 justify-center' : 'h-9 px-3',
        active
          ? 'rail-active font-semibold'
          : 'font-medium text-[var(--rail-text)] hover:bg-[var(--rail-2)] hover:text-[var(--rail-text-strong)]',
      )}
    >
      {item.icon}
      <span className={cn('truncate', collapsed && 'sr-only')}>{item.label}</span>
    </Link>
  )
}

/** "super_admin" → "Super admin": role keys are for code, not for people. */
function roleLabel(role: string): string {
  const words = role.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const RAIL_BUTTON =
  'grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-[var(--rail-muted)] transition-colors hover:bg-[var(--rail-2)] hover:text-[var(--rail-text-strong)]'

/**
 * Who is signed in, their own settings, and the way out.
 *
 * My account is here rather than in a section because every role has it,
 * including one whose sections are all hidden. The gear is the link a
 * keyboard or a screen reader finds; the picture and name are the same link
 * for a mouse, taken out of the tab order so it is not announced twice.
 */
function RailUser({ collapsed }: { collapsed: boolean }) {
  const { session, signOut } = useAuth()
  const { path } = useRouter()
  const name = session?.user.display_name ?? ''
  const avatar = session?.user.avatar_url ?? null
  const roles = (session?.roles ?? []).map(roleLabel).join(', ')
  const here = path === '/account'

  return (
    <div className="border-t border-[var(--rail-border)] p-3">
      <div className={cn('flex items-center gap-3', collapsed && 'flex-col gap-2')}>
        <Link
          to="/account"
          tabIndex={-1}
          aria-hidden
          title={collapsed ? `${name}${roles ? ` · ${roles}` : ''}` : 'My account'}
          className={cn(
            'flex min-w-0 items-center gap-3 rounded-[10px]',
            collapsed ? 'justify-center' : 'flex-1',
          )}
        >
          <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full bg-[var(--rail-2)] text-[12px] font-semibold text-[var(--rail-text-strong)] ring-1 ring-[var(--rail-border)]">
            {avatar ? (
              <img src={avatar} alt="" className="h-full w-full object-cover" />
            ) : (
              initials(name || '?')
            )}
          </span>
          <div className={cn('min-w-0 flex-1 leading-tight', collapsed && 'sr-only')}>
            <div className="truncate text-[13px] font-semibold text-[var(--rail-text-strong)]">
              {name}
            </div>
            <div className="truncate text-[12px] text-[var(--rail-muted)]">{roles}</div>
          </div>
        </Link>
        <Link
          to="/account"
          aria-label="My account"
          aria-current={here ? 'page' : undefined}
          title="My account"
          className={cn(RAIL_BUTTON, here && 'bg-[var(--rail-2)] text-[var(--rail-text-strong)]')}
        >
          <Settings className="h-4 w-4" aria-hidden />
        </Link>
        <button
          type="button"
          onClick={() => void signOut()}
          aria-label="Sign out"
          title="Sign out"
          className={RAIL_BUTTON}
        >
          <LogOut className="h-4 w-4 flip-rtl" aria-hidden />
        </button>
      </div>
    </div>
  )
}
